import React from 'react';
import { TeamCalendarEvent } from 'data/schemas/TeamCalendar';
import {
    buildCalendarCells,
    formatTimeLabel,
    getEventsForDate,
    isSameMonth,
    sameDate
} from './dateUtils';

interface Props {
    currentMonth: Date;
    events: TeamCalendarEvent[];
    selectedDate: Date;
    onSelectDate: (date: Date) => void;
    onSelectEvent: (event: TeamCalendarEvent) => void;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarMonthGrid(props: Props) {
    const { currentMonth, events, selectedDate, onSelectDate, onSelectEvent } = props;
    const cells = buildCalendarCells(currentMonth);
    const today = new Date();

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                {DAY_LABELS.map(label => (
                    <div
                        key={label}
                        className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                        {label}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {cells.map(date => {
                    const dayEvents = getEventsForDate(date, events).slice(0, 3);
                    const isSelected = sameDate(date, selectedDate);
                    const isCurrentMonth = isSameMonth(date, currentMonth);
                    const isToday = sameDate(date, today);

                    return (
                        <button
                            key={date.toISOString()}
                            type="button"
                            onClick={() => onSelectDate(date)}
                            className={`min-h-[8.5rem] border-b border-r border-gray-200 px-2 py-2 text-left transition hover:bg-blue-50 ${
                                isSelected ? 'bg-blue-50' : 'bg-white'
                            } ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                            <div className="mb-2 flex items-center justify-between">
                                <span
                                    className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                                        isToday
                                            ? 'bg-blue-600 text-white'
                                            : isSelected
                                              ? 'bg-blue-100 text-blue-700'
                                              : ''
                                    }`}
                                >
                                    {date.getDate()}
                                </span>
                                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                                    {dayEvents.length > 0 ? `${dayEvents.length} items` : ''}
                                </span>
                            </div>
                            <div className="space-y-1">
                                {dayEvents.map(event => (
                                    <div
                                        key={`${date.toISOString()}-${event.id}`}
                                        onClick={mouseEvent => {
                                            mouseEvent.stopPropagation();
                                            onSelectEvent(event);
                                        }}
                                        className="cursor-pointer rounded-md px-2 py-1 text-xs text-white shadow-sm"
                                        style={{ backgroundColor: event.color || '#2563eb' }}
                                    >
                                        <div className="truncate font-semibold">{event.title}</div>
                                        <div className="truncate opacity-90">
                                            {event.isAllDay ? 'All day' : formatTimeLabel(event.startAt)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default CalendarMonthGrid;
